import { Log } from '@apparel/core/log';
import { separate } from '@apparel/core/utils/strings/separate';
import * as H from 'history';
import { Location } from 'history';
import trim from 'lodash/trim';
import unset from 'lodash/unset';
import * as qs from 'query-string';
import { match } from 'react-router';
import { call } from '../../functions';
import { baseFilter } from '../../functions/lib/baseFilter';
import { flattenObject } from '../../objects/lib/flattenObject';
import { GetterSetter } from '../../objects/lib/getterSetter';

export type ParamsType = { [key: string]: any };
export type RouteMap = { [key: string]: string };

interface RouterProps {
    history: H.History;
    location: H.Location<Record<string, any>>;
    match: match;
}

export interface RouterServiceProviderImpl {
    _router: RouterProps;

    history: History;

    setRouter(router: RouterProps): void;

    register(name: string | string[], url: string): RouterServiceProvider;

    buildUrl(url: string, params: ParamsType): string;

    routeName(name: string): string;

    route(name: string, params?: ParamsType, allowUrls?: boolean): string;

    getRouteUrl(name: string): string;

    encodeValue(name: string): string;

    getParam(params: ParamsType, value: string): string;

    getAllRoutes(): RouteMap;

    isDefined(name: string): boolean;

    goTo(route: string, params?: { [key: string]: any }, state?: { [key: string]: any }): void;

    goBack(href?: string, props?: { [key: string]: any }): void;

    openWithWindow;
}

type NavigationStrategies = 'push' | 'replace';
type NavigationStrategyFlags = 'reload';
type Strategies = `${NavigationStrategies}` | `${NavigationStrategies},${NavigationStrategyFlags}`;

export class RouterServiceProvider extends GetterSetter {
    DEFAULT_NAME = 'home';
    COLLECTION_ROOT = '__routes__';

    log = Log.channel('RouterServiceProvider');

    _router!: RouterProps;

    /**
     * Set router
     * @param router
     */
    setRouter(router: RouterProps): void {
        this.log.debug('Register router', { router });
        this._router = router;
    }

    // @ts-ignore
    get history(): H.History {
        return this._router ? this._router.history : ({} as any);
    }

    /**
     * Register route
     * @param name
     * @param url
     * @returns {RouterServiceProvider}
     */
    register(name: string | string[], url: string): RouterServiceProvider {
        if (typeof name === 'object' && Array.isArray(name)) {
            name.forEach((name: string) => this.register(name, url));
        } else {
            // @ts-ignore
            this.set(this.routeName(name), url);
        }

        return this;
    }

    /**
     * Map Route name
     * @param name
     * @returns {string}
     */
    routeName(name: string): string {
        return `${this.COLLECTION_ROOT}.${name || this.DEFAULT_NAME}`;
    }

    /**
     * Build URL
     * @param url
     * @param params
     * @returns {*}
     */
    buildUrl(url: string, params: ParamsType = {}): string {
        const used: any[] = [];

        const url$ = `${url}/`
            .split('/')
            .map((seg) => {
                seg = seg.trim();

                seg = seg.replace(/\?$/, '');
                seg = seg.replace(/^:(.*?)$/gi, (found, value) => `{${trim(value)}}`);
                seg = seg.replace(/{:(.*?)}/gi, (found, value) => `{${trim(value)}}`);

                const beginIndex = seg.lastIndexOf('{');
                if (beginIndex > 0) {
                    seg = seg.substr(beginIndex);
                }

                const endIndex = seg.indexOf('}');
                if (endIndex > 0 && endIndex < seg.length) {
                    seg = seg.substr(0, endIndex + 1);
                }

                return seg;
            })
            .join('/')
            .replace(/({(.*?)})/g, (found, content, value: any) => {
                used.push(value);
                return `${this.getParam(params, value)}`;
            })
            .replace(/\/\//g, '/')
            .replace(/\/$/g, '')
            .replace(/\?$/g, '');

        used.forEach((key) => unset(params, `${key}`.trim()));

        const segments = url$.split('?');

        segments[1] = [segments[1], qs.stringify(params)].filter(baseFilter).join('&');

        return segments.filter(baseFilter).join('?').replace(/\/$/, '');
    }

    /**
     * @throws UndefinedRoute
     * @param name
     * @param params
     * @returns {string}
     */
    route(name: string, params: ParamsType = {}): string {
        if (!this.isDefined(name)) {
            return this.buildUrl(name, params);
        }

        return this.buildUrl(this.getRouteUrl(name), params);
    }

    /**
     * Get route defined URL
     * @param name
     * @returns {string}
     */
    getRouteUrl(name: string): string {
        // @ts-ignore
        return this.get<string>(this.routeName(name));
    }

    /**
     * Encode param value
     * @private
     * @param value
     * @returns {*}
     */
    encodeValue(value: string): string {
        return encodeURIComponent(value || '')
            .replace(/%40/gi, '@')
            .replace(/%3A/gi, ':')
            .replace(/%24/g, '$')
            .replace(/%2C/gi, ',')
            .replace(/%20/g, '+')
            .replace(/%5B/gi, '[')
            .replace(/%5D/gi, ']');
    }

    /**
     * @private
     * @param params
     * @param value
     * @returns {string}
     */
    getParam(params: ParamsType, value: string): string {
        value = `${value}`.trim();
        return params.hasOwnProperty(value) ? this.encodeValue(`${params[value]}`) : '';
    }

    /**
     * Get Sections
     * @param flatten
     * @returns {RouteMap}
     */
    getAllRoutes(flatten = false): RouteMap {
        // @ts-ignore
        const routes = this.__data.get(this.COLLECTION_ROOT)?.toJS() ?? {};

        if (flatten) {
            return flattenObject(routes);
        }

        return routes;
    }

    /**
     * Print routes
     */
    printRoutes(): void {
        if (Log.canLog()) {
            const routes = this.getAllRoutes(true);
            console.groupCollapsed('Router');
            this.log.debug('Defined routes');
            console.table(Object.entries(routes).map(([key, value]) => ({ key, value })));
            console.groupEnd();
        }
    }

    /**
     * Is Defined
     * @param name
     * @returns {boolean}
     */
    isDefined(name: string): boolean {
        // @ts-ignore
        return this.has(this.routeName(name));
    }

    resolveRoute(
        route: string | Location,
        params: { [key: string]: any } = {},
        state: { [key: string]: any } = {},
    ): ({ raw: any } & Location) | null {
        let route$: { raw: any } & Location;

        if (!route) {
            return null;
        }

        if (typeof route !== 'string') {
            const search = qs.parse(route?.search ?? '');
            route$ = {
                ...route,
                search: qs.stringify({ ...search, ...params }),
                state: { ...((route as any)?.state || {}), ...state },
                raw: route,
            };
        } else {
            const routeString = this.isDefined(route) ? this.route(route, params) : this.buildUrl(route, params);
            const routeStringSplit = routeString.split('?');

            route$ = {
                state,
                hash: '',
                pathname: routeStringSplit[0],
                search: `?${routeStringSplit[1] ?? ''}`,
                raw: routeString,
            };
        }

        return route$;
    }

    /**
     *
     * @param route
     * @param params
     * @param state
     * @param strategy
     */
    goTo(
        route: string | Location,
        params: { [key: string]: any } = {},
        state: { [key: string]: any } = {},
        strategy: Strategies | boolean = true,
    ): void {
        if (strategy === true) {
            strategy = 'push';
        } else if (strategy === false) {
            strategy = 'replace';
        }

        const strategies = separate(`${strategy}`.toLowerCase(), ',');

        this._resolveRouter((router) => {
            const route$: Location | null = this.resolveRoute(route, params, state);

            if (route$) {
                this.log.debug(`Navigate '${route$.pathname}'`, route$);

                // console.log(strategies);

                switch (true) {
                    case strategies.includes('push'):
                        router.history.push(route$);
                        break;

                    case strategies.includes('replace'):
                        router.history.replace(route$);
                        break;
                }

                if (strategies.includes('reload')) {
                    setTimeout(() => this.reload(), 50);
                }
            }
        });
    }

    createHref(
        route: string | Location,
        params?: null | Record<string, any>,
        state?: Record<string, any> | null,
    ): string {
        const route$ = this.resolveRoute(route, params ?? {}, state ?? {});

        if (typeof route === 'string' && /^((http|https):)?\/\//.test(route$?.raw)) {
            return route$?.raw;
        }

        return route$ ? this.history.createHref(route$) : '/';
    }

    /**
     * Go back
     */
    goBack(href?: string, props?: Record<string, any>): void {
        this._resolveRouter((router) => {
            if (!href) {
                this.log.debug('Go back');
                router.history.goBack();
            } else if (href) {
                href = this.route(href, props);
                this.log.debug('Navigate', href, 'POP');
                router.history.push(href);
            }
        });
    }

    /**
     * Go forward
     */
    goForward(): void {
        this._resolveRouter((router) => {
            this.log.debug('Go forward');
            router.history.goForward();
        });
    }

    /**
     * Run action when router it's resolved
     * @param onResolve
     * @private
     */
    _resolveRouter(onResolve: (router: RouterProps) => void): void {
        if (this._router) {
            call(onResolve, this._router);
        } else {
            setTimeout(() => this._resolveRouter(onResolve), 50);
        }
    }

    /**
     * Replace route
     * @param route
     * @param params
     * @param state
     */
    public replace(route, params?: any, state?: any): void {
        this.goTo(route, params, state, false);
    }

    public openWithWindow(href: any, params?: any, target?: string, features?: string, replace?: boolean) {
        if (typeof window !== 'undefined') {
            // @ts-ignore
            return window.open(this.createHref(href, params), target, features, replace);
        }

        return null;
    }

    private reload() {
        window.location.reload();
    }
}
